import { type EntityState } from '@ngrx/entity';

import {
  type CanvasElement,
  type ElementTargetPath,
  type SlotIdOf,
} from '@blaze-runtime/engine-domain';

import { getWrapperRefs } from '../../../canvas-elements/element-model/wrapper-refs';
import { getElementPath } from '../../../utils/canvas-element/path';
import {
  COMMIT_BUTTON_REFID,
  type FindFieldTargetPathData,
  findSlotForField,
  getColumnContainer,
  getLastGeneratedElementPath,
} from '../../../utils/elements/generation.utils';
import { getPathLastInSlot } from '../../../utils/layout/helpers';
import { isElementChildOf } from '../../../utils/layout/relationship';

import { type FormLikeElement } from './form-generation-data';

/**
 * Find the target path/where to put the field.
 * It can be either the form element itself or the column container
 * if there are many elements and they need to be spread into multiple columns.
 */
export function getFieldTargetPath(
  entityState: EntityState<CanvasElement>,
  data: FindFieldTargetPathData<FormLikeElement>,
): ElementTargetPath {
  const columnContainer = getColumnContainer(entityState.entities, data.element);
  const parentElement = columnContainer || data.element; // If present - use column container to be the parent for the field
  const columnCount = parentElement.slots.ids.length;
  const maxFieldsPerColumn = getMaxFieldsPerColumn(data.totalFields, columnCount, data.fieldIndex);
  const parentSlotId = findSlotForField(
    entityState.entities,
    parentElement,
    maxFieldsPerColumn,
    data.isColumnsReflow,
  ) as SlotIdOf<'container' | 'formContainer' | 'formDetails'>;

  let fieldTargetPath = getLastGeneratedElementPath(
    entityState.entities,
    data.element,
    parentElement.slots[parentSlotId],
  );
  if (fieldTargetPath && !data.isColumnsReflow) {
    // If last generated element found
    // The new field should be placed after the already existing one
    if (fieldTargetPath.index !== undefined) {
      fieldTargetPath.index++;
    }
    fieldTargetPath.targetPosition = { type: 'top' };
  } else if (columnContainer) {
    // If this is the first generated element in a column
    // Place the first generated element as last in column
    fieldTargetPath = getPathLastInSlot(entityState, parentElement.id, parentSlotId);
  } else {
    // No column container and nothing generated here yet: insert before the top-level child that is,
    // or contains, the commit button, so the field lands below the authored layout and above submit
    const commitButtonId = getWrapperRefs(data.element)[COMMIT_BUTTON_REFID];
    const commitButtonTopLevelChild = commitButtonId
      ? (parentElement.slots[parentSlotId]?.children ?? []).find(
          (childId) =>
            childId === commitButtonId ||
            isElementChildOf(entityState.entities, commitButtonId, childId),
        )
      : undefined;

    fieldTargetPath = commitButtonTopLevelChild
      ? getElementPath(entityState.entities, commitButtonTopLevelChild)
      : getPathLastInSlot(entityState, parentElement.id, parentSlotId);
  }

  if (!fieldTargetPath) {
    throw new Error('Could not determine field target path');
  }

  return fieldTargetPath;
}

/** Spreads the fields evenly over the columns, filling the leftmost ones first. */
function getMaxFieldsPerColumn(
  totalFields: number,
  columnCount: number,
  fieldIndex: number,
): number {
  const baseFieldsPerColumn = Math.floor(totalFields / columnCount);
  const extraFields = totalFields % columnCount;
  const columnIndex = Math.floor(fieldIndex / (baseFieldsPerColumn + (extraFields > 0 ? 1 : 0)));
  const fieldsInColumn = baseFieldsPerColumn + (columnIndex < extraFields ? 1 : 0);

  return fieldsInColumn;
}
